import { useAppContext } from '@features/contexts/AppProvider';
import {
  ClinicalDataToShare,
  ClinicalItemLogWithVitals,
  ClinicalItemLog,
  WithPerfusionTime,
} from '@features/components/ClinicalData/types';
import { groupClinicalDataItems } from '@features/components/ClinicalData/sections/utils';
import { liverAnatomyDescription } from '@features/components/ClinicalData/constants';
import {
  getPerfusionTerminationType,
  getIdentityAccessTicketTextFromClinicalLog,
} from '@features/components/ClinicalData/components/ClinicalLog/LogItems/utils';
import { WithChipAndPhotoItem } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/WithChipAndPhotoItem';
import {
  heartPassOptionLabels,
  sharedExternallyEvents,
} from '@features/components/ClinicalData/components/ClinicalLog/constants';
import { StatusChangeItem } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/StatusChangeItem';
import {
  getBaseEventVariant,
  getStatusEventVariant,
} from '@features/components/ClinicalData/components/ClinicalLog/utils';
import { YesNoItem } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/YesNoItem';
import { BaseEventItem } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/BaseEventItem';
import { VitalsItem } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/VitalsItem';
import { WithAttachmentsItem } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/WithAttachmentsItem';
import { PfRatioOnOCS } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/PfRatioOnOCS';
import { DryRunItem } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/DryRunItem';
import { BloodSampleItem } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/BloodSampleItem';
import { PfRatioInSitu } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/PfRatioInSitu';
import { ServiceConfirmationItem } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/ServiceConfirmationItem';
import { ClinicalLogItemTime } from '@features/components/ClinicalData/components';
import { useShareClinicalData } from '@features/components/ClinicalData';
import { useChatShare } from '@features/components/Chat/contexts/ChatShareContext';
import { joinWithNewline } from '@/utils/helpers/string.helpers';
import { ClinicalData, ClinicalItemType, OrganType } from '@/api/apis/case';
import { randomizationTypeNames } from '@/constants/case/case.constants';
import { getOrganApprovalMessage, getOrganDeclineMessage } from '../messageFormatters';

export type ClinicalLogItemProps = {
  record: WithPerfusionTime<ClinicalItemLog>;
  clinicalData: ClinicalData;
  organType: OrganType;
  isTrialCase: boolean;
  handleShareClick?: (activeReading?: ClinicalDataToShare) => void;
};

const dangerEvents = [ClinicalItemType.RecordDelay, ClinicalItemType.DonorDidNotExpireInTime, ClinicalItemType.DryRun];

export const ClinicalLogItemBase = ({
  record,
  clinicalData,
  organType,
  isTrialCase,
  handleShareClick,
}: ClinicalLogItemProps) => {
  const {
    clinicalLog: { names: clinicalLogNames },
  } = useAppContext();

  const { details, mediaFiles, yesNo, clinicalItemType } = record;
  const eventLabel: string = clinicalLogNames[clinicalItemType];
  const shared: boolean = sharedExternallyEvents.includes(clinicalItemType);
  const sharedOnOCSVideo = organType === OrganType.Lungs ? false : shared;

  const getLogComponent = () => {
    switch (clinicalItemType) {
      case ClinicalItemType.AtDonorHospital:
      case ClinicalItemType.OrganOnOCS:
      case ClinicalItemType.OrganOnSCS:
      case ClinicalItemType.LeavingDonorHospital:
      case ClinicalItemType.ArrivalAtRecipientHospital:
      case ClinicalItemType.OffOCS:
      case ClinicalItemType.OffSCS:
        return (
          <StatusChangeItem
            statusLabel={eventLabel}
            shared={shared}
            variant={getStatusEventVariant(clinicalItemType)}
            description={details}
            onShareClick={handleShareClick}
          />
        );
      case ClinicalItemType.ScsTerminationOption:
        return (
          <StatusChangeItem
            statusLabel={eventLabel}
            shared={shared}
            variant={getStatusEventVariant(clinicalItemType)}
            onShareClick={handleShareClick}
          />
        );
      case ClinicalItemType.DonorInOR:
      case ClinicalItemType.Heparin:
      case ClinicalItemType.CrossClampTimeFlushTime:
      case ClinicalItemType.SkinIncisionTime:
      case ClinicalItemType.NRPStart:
      case ClinicalItemType.PreOCSFlush:
      case ClinicalItemType.PFOValveCondition:
      case ClinicalItemType.Priming:
      case ClinicalItemType.FinalFlush:
      case ClinicalItemType.LeavingRecipientHospital:
      case ClinicalItemType.CardiacArrest:
      case ClinicalItemType.DonorDidNotExpireInTime:
      case ClinicalItemType.RecordDelay:
      case ClinicalItemType.WithdrawalOfLifeSupportTime:
        return (
          <BaseEventItem
            event={eventLabel}
            description={details}
            variant={getBaseEventVariant(clinicalItemType)}
            shared={shared}
            onShareClick={handleShareClick}
          />
        );
      case ClinicalItemType.Randomization:
        return (
          <BaseEventItem
            event={eventLabel}
            description={record.result ? randomizationTypeNames[record.result] : ''}
            shared={shared}
            onShareClick={handleShareClick}
          />
        );
      case ClinicalItemType.DryRun:
        return (
          <DryRunItem
            event={eventLabel}
            description={details}
            dispositionReason={record.dispositionReason}
            dispositionSpecification={record.dispositionSpecification}
            shared={shared}
            onShareClick={handleShareClick}
          />
        );

      case ClinicalItemType.ScheduledDonorORStartTime:
      case ClinicalItemType.OrganAccepted:
        return (
          <BaseEventItem
            event={eventLabel}
            description={details}
            shared={shared}
            variant={getBaseEventVariant(clinicalItemType)}
          />
        );

      case ClinicalItemType.DonorLabelsCheck:
      case ClinicalItemType.BloodAvailabilityCheck:
      case ClinicalItemType.BronchCartAndSuctionCheck:
      case ClinicalItemType.ReintubationPersonalCheck:
      case ClinicalItemType.OnSiteCoordinatorsContacts:
      case ClinicalItemType.ResearchBloodSamples:
      case ClinicalItemType.TeamHuddle:
      case ClinicalItemType.CysticDuct:
      case ClinicalItemType.PickUpOfOrganLabelAndTissueSamples:
      case ClinicalItemType.IceForTheOCSCoolerPriorDeparture:
        return (
          <YesNoItem
            event={eventLabel}
            description={details}
            value={yesNo}
            shared={shared}
            onShareClick={handleShareClick}
          />
        );

      case ClinicalItemType.PreOCSBloodSample:
      case ClinicalItemType.BloodSample:
        return (
          <BloodSampleItem
            typeLabel={eventLabel}
            item={record}
            onShareClick={handleShareClick}
            details={details}
            shared={shared}
          />
        );

      case ClinicalItemType.PFRatioInSitu:
        return (
          <PfRatioInSitu
            event={eventLabel}
            item={record}
            onShareClick={handleShareClick}
            isTrialCase={isTrialCase}
            shared={shared}
          />
        );

      case ClinicalItemType.PFRatioOnOCSDonorHospital:
      case ClinicalItemType.PFRatioOnOCSTXCenter:
        return (
          <PfRatioOnOCS
            event={eventLabel}
            onShareClick={handleShareClick}
            item={record}
            isTrialCase={isTrialCase}
            shared={shared}
          />
        );

      case ClinicalItemType.OnOCSVideo:
        return (
          <WithAttachmentsItem
            event={eventLabel}
            description={details}
            mediaFiles={mediaFiles}
            yesNoValue={yesNo}
            shared={sharedOnOCSVideo}
            onShareClick={handleShareClick}
          />
        );

      case ClinicalItemType.VisualInspection:
      case ClinicalItemType.PostFlushBronch:
      case ClinicalItemType.PostFlushLungsPhoto:
      case ClinicalItemType.BronchInSitu:
      case ClinicalItemType.LiverOnABackBench:
      case ClinicalItemType.AnatomyPhotos:
      case ClinicalItemType.ReconstructionVideosPhotos:
      case ClinicalItemType.InSituVideoOfHeart:
      case ClinicalItemType.Stabilization:
      case ClinicalItemType.BronchVideoOnTheOCS:
      case ClinicalItemType.FinalCheck:
      case ClinicalItemType.FinalBronchVideoAtTheRecipientHospital:
      case ClinicalItemType.FinalPhysicalExamPictureOrVideo:
      case ClinicalItemType.OffOCSPhotoAndVideo:
      case ClinicalItemType.Bile:
      case ClinicalItemType.OrganMonitoring:
      case ClinicalItemType.DonorBiopsyReport:
      case ClinicalItemType.TxOCSBiopsy:
        return (
          <WithAttachmentsItem
            event={eventLabel}
            description={details}
            mediaFiles={mediaFiles}
            yesNoValue={yesNo}
            shared={shared}
            onShareClick={handleShareClick}
          />
        );
      case ClinicalItemType.VitalUpdates:
        return (
          <VitalsItem
            onShareClick={handleShareClick}
            vitals={(groupClinicalDataItems(clinicalData, record) as ClinicalItemLogWithVitals)?.items}
            event={eventLabel}
            shared={shared}
          />
        );
      case ClinicalItemType.DeathDeclarationTime:
        return (
          <WithChipAndPhotoItem
            shared
            event={eventLabel}
            description={details}
            mediaFiles={mediaFiles}
            onShareClick={handleShareClick}
          />
        );
      case ClinicalItemType.LiverAnatomyQuestionnaire:
        return (
          <BaseEventItem
            event={eventLabel}
            description={joinWithNewline(liverAnatomyDescription[record.answer], record.text ?? '', details ?? '')}
            shared={shared}
            onShareClick={handleShareClick}
          />
        );
      case ClinicalItemType.LiverReconstructionQuestionnaire:
        return (
          <YesNoItem
            event={record.answer ? `${eventLabel}: ${record.answer}` : eventLabel}
            description={record.text || details}
            value={yesNo}
            shared={shared}
            onShareClick={handleShareClick}
          />
        );

      case ClinicalItemType.BloodCollection:
        return (
          <BaseEventItem
            shared
            onShareClick={handleShareClick}
            event={eventLabel}
            description={record.volume ? `${record.volume}ml. ${details}` : details}
          />
        );

      case ClinicalItemType.ServiceConfirmation:
        return (
          <ServiceConfirmationItem
            surgeon={record.retrievalSurgeon}
            managment={record.ocsSpecialist}
            txApprover={record.txRepresentative}
            shared={shared}
          />
        );
      case ClinicalItemType.MultipleDonorSpecimenHandoff:
      case ClinicalItemType.DonorSpecimenHandoff:
        return (
          <WithAttachmentsItem
            onShareClick={handleShareClick}
            event={eventLabel}
            description={record.signature?.fullName}
            mediaFiles={mediaFiles}
            shared={shared}
          />
        );

      case ClinicalItemType.HeartPassOption:
        return (
          <BaseEventItem
            onShareClick={handleShareClick}
            event={heartPassOptionLabels[record.heartPassOptionType!]}
            description={details}
            shared={shared}
          />
        );

      case ClinicalItemType.PerfusionTerminationOption:
        return (
          <BaseEventItem
            event={getPerfusionTerminationType(record.details) ?? ''}
            shared
            onShareClick={handleShareClick}
          />
        );

      case ClinicalItemType.IdentityAccessTicketGranted:
      case ClinicalItemType.IdentityAccessTicketRevoked:
        return (
          <BaseEventItem
            event={eventLabel}
            description={getIdentityAccessTicketTextFromClinicalLog(record)}
            shared={shared}
            onShareClick={handleShareClick}
          />
        );
      case ClinicalItemType.OrganApproved:
        return (
          <BaseEventItem
            event={eventLabel}
            description={getOrganApprovalMessage(record)}
            shared={shared}
            variant="dark"
            onShareClick={handleShareClick}
          />
        );
      case ClinicalItemType.OrganDeclined:
        return (
          <BaseEventItem
            event={eventLabel}
            description={getOrganDeclineMessage(record)}
            shared={shared}
            variant="dark"
            onShareClick={handleShareClick}
          />
        );
      default:
        return null;
    }
  };

  const logComponent = getLogComponent();
  if (!logComponent) return null;
  return (
    <div className="relative flex gap-1">
      <div className="absolute left-8 top-0 -bottom-4 w-px bg-base-40" />
      <ClinicalLogItemTime
        time={{ timeZone: record.timeZone, timestamp: record.timestamp }}
        perfusionTime={record.perfusionTime}
        variant={dangerEvents.includes(record.clinicalItemType) ? 'danger' : 'default'}
      />
      {logComponent}
    </div>
  );
};

export const SharableClinicalLogItem = (props: Omit<ClinicalLogItemProps, 'handleShareClick' | 'canShare'>) => {
  const { record, isTrialCase } = props;
  const { channelsExist } = useChatShare();
  const handleShareClick = useShareClinicalData(record, isTrialCase);

  return <ClinicalLogItemBase {...props} handleShareClick={channelsExist ? handleShareClick : undefined} />;
};

export const ClinicalLogItem = ({
  hasChatPermission,
  ...props
}: Omit<ClinicalLogItemProps, 'handleShareClick' | 'canShare'> & { hasChatPermission: boolean }) => {
  if (hasChatPermission) {
    return <SharableClinicalLogItem {...props} />;
  }

  return <ClinicalLogItemBase {...props} />;
};
